import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Facebook, Instagram, Send, Check } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contacts`, formData);
      setSubmitted(true);
      toast.success('Mensagem enviada com sucesso!');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1529900748604-07564a03e7a6?auto=format&fit=crop&q=80')`,
          }}
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 text-center section-padding">
          <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">Fale Connosco</span>
          <h1 className="font-heading text-5xl md:text-7xl font-black mt-2">CONTACTOS</h1>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="py-24 section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div>
              <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">Informação</span>
              <h2 className="font-heading text-4xl font-black mt-2 mb-8">ENTRE EM CONTACTO</h2>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 border border-white/20 flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold mb-1">MORADA</h3>
                    <p className="text-gray-400">
                      Rua José Loureiro Duarte, 2<br />
                      Esporões - Braga
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 border border-white/20 flex items-center justify-center flex-shrink-0">
                    <Phone size={20} />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold mb-1">TELEFONE</h3>
                    <a href="tel:913789152" className="text-gray-400 hover:text-white transition-colors">
                      913 789 152
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 border border-white/20 flex items-center justify-center flex-shrink-0">
                    <Mail size={20} />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold mb-1">EMAIL</h3>
                    <a href="mailto:geral@gdresporoes.pt" className="text-gray-400 hover:text-white transition-colors">
                      geral@gdresporoes.pt
                    </a>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-12">
                <h3 className="font-heading font-bold mb-4">REDES SOCIAIS</h3>
                <div className="flex gap-4">
                  <a
                    href="https://www.facebook.com/gdresporoes"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-all"
                    data-testid="social-facebook"
                  >
                    <Facebook size={20} />
                  </a>
                  <a
                    href="https://www.instagram.com/gdresporoes"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-all"
                    data-testid="social-instagram"
                  >
                    <Instagram size={20} />
                  </a>
                </div>
              </div>

              {/* Map */}
              <div className="mt-12">
                <h3 className="font-heading font-bold mb-4">LOCALIZAÇÃO</h3>
                <div className="aspect-video bg-[#0A0A0A] border border-white/10 overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d11916.06749655877!2d-8.437!3d41.5167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd24fee16e3e0cf1%3A0x500ebbde490c660!2sEspor%C3%B5es%2C%20Braga!5e0!3m2!1spt-PT!2spt!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0, filter: 'grayscale(100%) invert(90%)' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Localização GDR Esporões"
                  />
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-[#0A0A0A] border border-white/10 p-8 md:p-12">
                <h3 className="font-heading text-2xl font-bold mb-6">ENVIE UMA MENSAGEM</h3>
                
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-6 border-2 border-green-500 rounded-full flex items-center justify-center">
                      <Check size={32} className="text-green-500" />
                    </div>
                    <h4 className="font-heading text-xl font-bold mb-2">Mensagem Enviada!</h4>
                    <p className="text-gray-400">Entraremos em contacto assim que possível.</p>
                    <Button
                      onClick={() => setSubmitted(false)}
                      className="mt-6"
                      variant="outline"
                    >
                      Enviar Nova Mensagem
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">
                        Nome *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-black/50 border-white/10 focus:border-white/30 rounded-none"
                        placeholder="O seu nome"
                        data-testid="contact-name"
                      />
                    </div>

                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">
                        Email *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-black/50 border-white/10 focus:border-white/30 rounded-none"
                        placeholder="O seu email"
                        data-testid="contact-email"
                      />
                    </div>

                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">
                        Assunto *
                      </label>
                      <Input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="bg-black/50 border-white/10 focus:border-white/30 rounded-none"
                        placeholder="Assunto da mensagem"
                        data-testid="contact-subject"
                      />
                    </div>

                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">
                        Mensagem *
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="bg-black/50 border-white/10 focus:border-white/30 rounded-none resize-none"
                        placeholder="A sua mensagem"
                        data-testid="contact-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-white text-black hover:bg-gray-200 rounded-none uppercase font-bold tracking-widest py-6"
                      data-testid="contact-submit"
                    >
                      {loading ? (
                        'A ENVIAR...'
                      ) : (
                        <>
                          ENVIAR MENSAGEM <Send size={16} className="ml-2" />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
